/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ListenerMiddlewareInstance } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import { dismissAlertOverlay } from "../alertOverlay";
import type { UnityAppDispatch } from "../UnityAppStore";
import {
  recoverByReloading,
  StartupRecoveryAction
} from "./postSigninStartupSlice";

const TAG: string = "startupRecoveryListeners";

/**
 * Acts on the escape-hatch buttons offered by the post-sign-in startup
 * overlay.
 *
 * The overlay is raised from `callCritical` inside the startup thunk, and the
 * retry behind it never gives up — right for a service that may come back,
 * wrong for one that cannot. A TSRI catalog entry pointing at an unresolvable
 * host stays broken however long the app waits, so after a few failures the
 * overlay grows a Try Again button and these listeners carry it out.
 *
 * Registered at the store rather than owned by a screen: the protected area
 * mounts as soon as the user is authenticated, so there is no one screen
 * guaranteed to be on-screen when the user presses the button. A store
 * listener answers wherever they are.
 *
 * @param listenerMiddleware - The store's listener middleware instance.
 */
export function registerStartupRecoveryListeners(
  listenerMiddleware: ListenerMiddlewareInstance
): void {
  listenerMiddleware.startListening({
    actionCreator: dismissAlertOverlay,
    effect: (action, listenerApi): void => {
      const { dispatch } = listenerApi as unknown as {
        dispatch: UnityAppDispatch;
      };
      const actionId: string = action.payload;

      if (actionId === StartupRecoveryAction.RETRY) {
        Log.i(TAG, "recovery: Try Again");
        dispatch(recoverByReloading());
      }
    }
  });
}
